import { createContext, useContext, useState, useEffect } from 'react'
import authService from '../services/auth'

const AuthContext = createContext(null)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sessionToken, setSessionToken] = useState(null)

  // Check if user is already logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token')
      if (token) {
        try {
          const userData = await authService.getCurrentUser()
          setUser(userData)
        } catch (error) {
          // Token is invalid, clear it
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
        }
      }
      setLoading(false)
    }

    initAuth()
  }, [])

  const loginStep1 = async (email, password) => {
    const data = await authService.loginStep1(email, password)
    setSessionToken(data.session_token)
    return data
  }

  const loginStep2 = async (code, isBackupCode = false) => {
    if (!sessionToken) {
      throw new Error('No session token available. Please complete step 1 first.')
    }

    const data = await authService.loginStep2(sessionToken, code, isBackupCode)

    // Store tokens
    localStorage.setItem('access_token', data.access)
    localStorage.setItem('refresh_token', data.refresh)

    // Set user
    setUser(data.user)
    setSessionToken(null)

    return data
  }

  const logout = async () => {
    await authService.logout()
    setUser(null)
    setSessionToken(null)
  }

  const acceptInvitationStep1 = async (data) => {
    const response = await authService.acceptInvitationStep1(data)
    setSessionToken(response.session_token)
    return response
  }

  const acceptInvitationStep2 = async (code) => {
    if (!sessionToken) {
      throw new Error('No session token available. Please complete step 1 first.')
    }

    const data = await authService.acceptInvitationStep2(sessionToken, code)

    // Store tokens
    localStorage.setItem('access_token', data.tokens.access)
    localStorage.setItem('refresh_token', data.tokens.refresh)

    // Set user
    setUser(data.user)
    setSessionToken(null)

    return data
  }

  const value = {
    user,
    loading,
    sessionToken,
    loginStep1,
    loginStep2,
    logout,
    acceptInvitationStep1,
    acceptInvitationStep2,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
